import { Component, Inject, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { IFieldConfig } from "src/app/domain/IFieldConfig";
import { TypeCesEnum } from "src/app/domain/registration/enums/type-ces.enum";
import { MERCHANT_CONFIG_FIELDS } from "../../../merchants/crud-config";
import { catchError, filter, map, Observable, of, switchMap, take, tap } from "rxjs";
import { MerchantService } from "../../../merchants/crud-service";
import { ToastService } from "src/app/shared/services/toast/toast.service";
import { LegalPersonalityEnum } from "src/app/domain/enums/legal-personality.enum";
import { AbstractCrudCES } from "src/app/shared/components/crud/abstract-crud-ces";
import { IMerchantPartnersFormData } from "./dialog-merchant-partners-interface";
import { MerchantValidatorService } from "./merchant-validator.service";


@Component({
    selector: 'app-dialog-merchant-partner',
    templateUrl: './dialog-merchant.component.html',
    styleUrls: ['./dialog-merchant.component.sass'],
})
export class DialogMerchantComponent extends AbstractCrudCES<IMerchantPartnersFormData> implements OnInit {

    merchantForm: FormGroup;
    override config: IFieldConfig[] = MERCHANT_CONFIG_FIELDS;
    override typeCes: TypeCesEnum = TypeCesEnum.NEW;
    private isManualAddressMode = false;

    constructor(
        public override fb: FormBuilder,
        public dialogRef: MatDialogRef<DialogMerchantComponent>,
        private merchantService: MerchantService,
        private toastService: ToastService,
        private merchantValidator: MerchantValidatorService,
        @Inject(MAT_DIALOG_DATA) public override data: any
    ) {
        super(fb);
        this.merchantForm = this.fb.group({
            responsavelNome: ['', [Validators.required]],
            responsavelCpf: ['', [Validators.required, Validators.pattern(/^\d{11}$/)]],
            responsavelRg: ['', [Validators.required]],
            responsavelNacionalidade: ['Brasileiro', [Validators.required]],
            responsavelCelular: ['', [Validators.required, Validators.pattern(/^\d{11}$/)]],
            responsavelEmail: ['', [Validators.required, Validators.email]],
            responsavelDataNascimento: ['', [Validators.required]],
            responsavelCep: ['', [Validators.required, Validators.pattern(/^\d{8}$/)]],
            responsavelEstado: ['', [Validators.required]],
            responsavelCidade: ['', [Validators.required]],
            responsavelEndereco: ['', [Validators.required]],
            responsavelNumero: ['', [Validators.required]],
            responsavelBairro: ['', [Validators.required]],
            responsavelComplemento: [''],
            idExterno: ['', [Validators.required]],
            tipoConta: ['', [Validators.required]],
            validarIp: [false],
            ips: [''],
            chavePix: ['', []],
            celularFinanceiro: ['', [Validators.required]],
            emailFinanceiro: ['', [Validators.required]],
        });
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.mapearDadosParaFormulario(this.data);
        if(this.data?.typeCes === this.typeCesEnum.SHOW){
            this.typeCes = this.data.typeCes;
            this.merchantForm.disable();
        }
        this.loadStates();
    }

    private mapearDadosParaFormulario(data: any): void {
        if (!data) {
            return;
        }

        // Se já temos dados do modal preenchidos anteriormente, usar eles
        if (data.merchantData) {
            this.merchantForm.patchValue(data.merchantData);
            return;
        }

        // Caso contrário, preencher com dados do parceiro (apenas para PF)
        if (data.legalPersonality === LegalPersonalityEnum.PESSOA_FISICA) {
            this.merchantForm.patchValue({
                responsavelNome: data.name,
                responsavelCpf: data.cpf,
                responsavelRg: data.generalRegistration,
                responsavelCelular: data.cellPhone,
                responsavelEmail: data.email,
                responsavelDataNascimento: data.birthday,
                responsavelEstado: data.state,
                responsavelCidade: data.city,
                responsavelEndereco: data.address,
                responsavelNumero: data.addressNumber,
                responsavelBairro: data.neighborhood,
                responsavelComplemento: data.addressComplement,
            });
        }
    }

    public override getFieldConfig(name: string): any {
        return this.config.find((c) => c.name === name);
    }

    private c(name: string): AbstractControl {
        return this.merchantForm.get(name)!;
    }

    private fName(prefix: '' | 'responsavel', name: string): string {
        return prefix ? `responsavel${name}` : name.charAt(0).toLowerCase() + name.slice(1);
    }

    public getCitiesByUf(uf: string): Observable<any[]> {
        return this.merchantService.getCitiesByUf(uf);
    }

    private setOptions(fieldName: string, options: any[]): void {
        const field = this.getFieldConfig(fieldName);
        if (field) field.options = options;
    }

    private setFallbackField(fieldName: string, label: string): void {
        const field = this.getFieldConfig(fieldName);
        if (field && field.fallbackType) {
            field.type = field.fallbackType;
            field.label = field.fallbackLabel || label;
            field.placeholder = field.fallbackPlaceholder || `Digite ${label.toLowerCase()}`;
            field.options = [];
        }
    }

    private patchAddress(prefix: '' | 'responsavel', result: any): void {
        const patch: any = {};
        patch[this.fName(prefix, 'Endereco')] = result.address;
        patch[this.fName(prefix, 'Bairro')] = result.neighborhood;
        patch[this.fName(prefix, 'Cidade')] = result.city;
        patch[this.fName(prefix, 'Estado')] = result.state;
        this.merchantForm.patchValue(patch);
    }

    protected handleCepChange(prefix: '' | 'responsavel'): void {
        const value: string = this.merchantForm.get('responsavelCep')?.value;
        if (!value || value.length !== 8) return;

        this.merchantService.getCepInfo(value).pipe(
            take(1),
            tap((result: any) => {
                if (this.isManualAddressMode) {
                    this.patchAddress(prefix, result);
                    return;
                }
            }),
            filter(() => !this.isManualAddressMode),
            switchMap((result: any) =>
                this.getCitiesByUf(result.state).pipe(
                    take(1),
                    tap((resp: any[]) => {
                        const cityField = this.fName(prefix, 'Cidade');
                        this.c(cityField).reset();
                        this.setOptions(cityField, resp.map(v => v.nome));
                        this.patchAddress(prefix, result);
                    }),
                    catchError(err => {
                        console.error('Erro ao carregar cidades do CEP:', err);
                        this.setFallbackField(this.fName(prefix, 'Cidade'),
                            prefix ? 'Cidade do Responsável' : 'Cidade');
                        this.patchAddress(prefix, result);
                        return of(null);
                    })
                )
            ),
            catchError(err => {
                console.error('Erro ao consultar CEP:', err);
                this.toastService.showWarn('CEP não encontrado ou erro na consulta.');
                return of(null);
            })
        ).subscribe();
    }

    private loadStates(): void {
        this.merchantService.getStates().pipe(
            take(1),
            map((states: any[]) => states.map(s => s.sigla)),
            tap((stateOptions) => {
                this.setOptions('estado', stateOptions);
                this.setOptions('responsavelEstado', stateOptions);
            }),
            catchError(err => {
                console.error('Erro ao carregar estados:', err);
                return of([]);
            })
        ).subscribe();
    }

    add(): void {
        if (this.merchantForm.invalid) {
            this.toastService.showWarn('Por favor, preencha todos os campos obrigatórios do Comerciante corretamente.');
            this.markFormGroupTouched(this.merchantForm);
            return;
        }

        // Validar os dados usando o serviço de validação
        const merchantData = this.merchantForm.value;
        const validationErrors = this.merchantValidator.validate(merchantData, this.data);

        if (validationErrors.length > 0) {
            // Mostra apenas os 3 primeiros erros
            const errorMessages = validationErrors.slice(0, 3).map(e => `• ${e.field}: ${e.message}`).join('\n');
            const moreErrors = validationErrors.length > 3 ? `\n... e mais ${validationErrors.length - 3} erro(s)` : '';

            this.toastService.showError(`Erros de validação:\n${errorMessages}${moreErrors}`);
            return;
        }

        this.dialogRef.close(this.merchantForm.value);
    }

    private markFormGroupTouched(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            control?.markAsTouched();

            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            }
        });
    }

    cancel(): void {
        this.dialogRef.close();
    }

    // Preencher com dados mockados para teste
    fillMockData(): void {
        this.merchantForm.patchValue({
            chavePix: '62993564030',
            celularFinanceiro: '62993564030',
            emailFinanceiro: 'financeiro@teste.com',
            responsavelNome: 'João da Silva Santos',
            responsavelCpf: '12345678909',
            responsavelRg: '1234567',
            responsavelNacionalidade: 'Brasileiro',
            responsavelCelular: '62993564030',
            responsavelEmail: 'joao.silva@teste.com',
            responsavelDataNascimento: '1990-01-15',
            responsavelCep: '75133510',
            responsavelEstado: 'GO',
            responsavelCidade: 'Anápolis',
            responsavelBairro: 'Jardim das Flores',
            responsavelEndereco: 'Rua das Flores',
            responsavelNumero: '123',
            responsavelComplemento: 'Casa',
            idExterno: 'TEST' + Date.now(),
            tipoConta: 'liquidacao',
            validarIp: false,
            ips: ''
        });

        this.toastService.showSuccess('Dados de teste preenchidos com sucesso!');
    }

    // Buscar dados do CPF usando Brasil API (ReceitaWS)
    protected handleCpfChange(): void {
        const cpf = this.merchantForm.get('responsavelCpf')?.value;

        if (!cpf || cpf.length !== 11) return;

        // Validar CPF antes de buscar
        if (!this.isValidCPF(cpf)) {
            this.toastService.showWarn('CPF inválido');
            return;
        }

        this.toastService.showInfo('Buscando dados do CPF...');

        // Simulação de busca - em produção você pode integrar com ReceitaWS ou outra API
        // Por enquanto, vou apenas mostrar uma mensagem
        setTimeout(() => {
            this.toastService.showWarn('Busca automática por CPF não disponível nesta versão. Use o botão de teste para preencher dados mockados.');
        }, 1000);
    }

    private isValidCPF(cpf: string): boolean {
        if (!cpf) return false;
        cpf = cpf.replace(/\D/g, '');

        if (cpf.length !== 11) return false;
        if (/^(\d)\1+$/.test(cpf)) return false;

        // Validação do primeiro dígito
        let sum = 0;
        for (let i = 0; i < 9; i++) {
            sum += parseInt(cpf.charAt(i)) * (10 - i);
        }
        let digit = 11 - (sum % 11);
        if (digit >= 10) digit = 0;
        if (digit !== parseInt(cpf.charAt(9))) return false;

        // Validação do segundo dígito
        sum = 0;
        for (let i = 0; i < 10; i++) {
            sum += parseInt(cpf.charAt(i)) * (11 - i);
        }
        digit = 11 - (sum % 11);
        if (digit >= 10) digit = 0;
        if (digit !== parseInt(cpf.charAt(10))) return false;

        return true;
    }
}